import { ChangeEvent, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useAddMoney, useFeeFactor } from '@shared/hooks';
import type { GetAllCardsResponse } from '@shared/api';
import { Approval, Error, Form, Step, Success, Wizard } from './widgets';
import { Modal } from '@shared/widgets';

const formatCardNumber = (number: string) => (number ? '**** **** **** ' + number.slice(-4) : '');

const CURRENCIES = {
  USD: '$',
  EUR: '€',
};

interface AddMoneyType {
  card: GetAllCardsResponse | null;
  onClose: (value: SetStateAction<GetAllCardsResponse | null>) => void;
}

export const AddMoney = (props: AddMoneyType) => {
  const [step, setStep] = useState('form');
  const [amount, setAmount] = useState('');

  const { getFeeFactor, feeFactor } = useFeeFactor();
  const { loading, post, approvalUrl, reset } = useAddMoney();

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value), []);

  const currencyCode = props.card ? props.card.currency : '';

  const currency = CURRENCIES[currencyCode as keyof typeof CURRENCIES] || currencyCode;

  const fee = useMemo(() => (feeFactor && amount ? feeFactor * parseFloat(amount) : ''), [amount, feeFactor]);

  const finalAmount = useMemo(() => (amount && fee ? (parseFloat(amount) + parseFloat(fee as unknown as string)).toFixed(2) : ''), [amount, fee]);

  const finalFee = useMemo(() => (fee ? fee.toFixed(2) : ''), [fee]);

  const feeText = useMemo(() => (feeFactor ? feeFactor * 100 : null), [feeFactor]);

  const handlePost = useCallback(() => {
    if (!props.card) return;
    post({ cardId: props.card.id, amount: +finalAmount });
  }, [finalAmount, props.card, post]);

  useEffect(() => {
    if (!props.card) {
      setAmount('');
      reset();
      setStep('form');
    } else {
      getFeeFactor();
    }
  }, [props.card, reset, getFeeFactor]);

  useEffect(() => {
    if (approvalUrl) setStep('approval');
  }, [approvalUrl]);

  useEffect(() => {
    if (step === 'success' || step === 'error') {
      reset();
    }
  }, [step, reset]);

  const titleContent = step === 'form' || step === 'approval' ? 'Add money' : '';
  const title = titleContent ? titleContent : undefined;

  const content = (
    <Wizard step={step}>
      <Step step='form'>
        <Form
          cardNumber={props.card ? formatCardNumber(props.card.cardNumber) : ''}
          currency={currency}
          amount={amount}
          finalAmount={finalAmount}
          finalFee={finalFee}
          onPost={handlePost}
          loading={loading}
          onChange={handleChange}
        />
      </Step>
      <Step step='approval'>
        <Approval url={approvalUrl} onSetStep={setStep} />
      </Step>
      <Step step='success'>
        <Success amount={amount} currency={currency} fee={feeText} finalFee={finalFee} finalAmount={finalAmount} onClose={props.onClose} />
      </Step>
      <Step step='error'>
        <Error onAction={() => setStep('form')} />
      </Step>
    </Wizard>
  );

  return (
    <>
      <Modal
        title={title}
        titleClassName='!font-semibold !text-5xl'
        isOpen={!!props.card}
        onClose={props.onClose as (value?: SetStateAction<GetAllCardsResponse | null> | undefined) => void}
        width='600px'
      >
        <div className='max-md:text-[#4c4c4c80]'>{content}</div>
      </Modal>
    </>
  );
};
